/**
 * App Context
 * Unified state management for auth, analytics, and chat
 *
 * Security: Uses httpOnly cookie sessions instead of localStorage
 * - Session is validated via /api/auth/me on mount
 * - No sensitive data stored in browser-accessible storage
 */

import { createContext, useContext, useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import type { AnalyticsData, Message, ChartSpec } from '@resova/shared';
import {
  fetchAnalytics as apiFetchAnalytics,
  sendChatMessage,
  login as apiLogin,
  logout as apiLogout,
  getSession,
  initWithResovaToken,
  getChatSessions,
  getChatSession,
  deleteChatSession,
  getFavorites as apiGetFavorites,
  addFavorite as apiAddFavorite,
  deleteFavorite as apiDeleteFavorite,
  updateFavorites as apiUpdateFavorites,
  type SyncInfo,
  type UserInfo,
  type AccountInfo,
  type ChatSession,
  type FavoritePrompt,
  type AddFavoriteParams,
} from '@/lib/api';

// ============================================
// Types
// ============================================

interface AppState {
  // Auth
  isAuthenticated: boolean;
  isInitializing: boolean;
  /** True when both the existing ResovAI session check and the Resova cookie
   *  init attempt have failed. AuthGate uses this to redirect the user to the
   *  Resova login page (RP-2567). */
  requiresResovaLogin: boolean;
  user: UserInfo | null;
  account: AccountInfo | null;
  impersonating: { viewOnly: boolean; accountName?: string } | null;

  // Analytics
  analyticsData: AnalyticsData | null;
  analyticsLoading: boolean;
  analyticsError: string | null;
  dateRange: string;
  syncInfo: SyncInfo | null;

  // Chat
  conversationHistory: Message[];
  chatLoading: boolean;
  chatError: string | null;
  chatStreamingStatus: string | null;  // Status message during streaming
  currentSessionId: string | null;
  chatSessions: ChatSession[];
  chatSessionsLoading: boolean;

  // Favorites
  favoritePrompts: FavoritePrompt[];
  favoritesLoading: boolean;
}

interface AppContextType extends AppState {
  // Derived
  userName: string | null;

  // Auth actions
  login: (apiKey: string) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  setUser: (user: UserInfo | null) => void;
  setAccount: (account: AccountInfo | null) => void;
  setImpersonating: (impersonating: AppState['impersonating']) => void;
  setRequiresResovaLogin: (value: boolean) => void;

  // Analytics actions
  fetchAnalytics: (dateRange?: string) => Promise<void>;
  refreshAnalytics: () => Promise<void>;
  setDateRange: (dateRange: string) => void;

  // Chat actions
  sendMessage: (message: string) => Promise<string | undefined>;
  clearConversation: () => void;
  setConversationHistory: (messages: Message[]) => void;
  loadChatSessions: () => Promise<void>;
  loadChatSession: (sessionId: string) => Promise<void>;
  deleteSession: (sessionId: string) => Promise<void>;
  startNewSession: () => void;

  // Favorites actions
  loadFavorites: () => Promise<void>;
  addFavorite: (params: AddFavoriteParams) => Promise<boolean>;
  removeFavorite: (id: string) => Promise<void>;
  updateFavorites: (favorites: FavoritePrompt[]) => Promise<void>;
  isFavorite: (text: string) => boolean;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

// ============================================
// Provider
// ============================================

const initialState: AppState = {
  isAuthenticated: false,
  isInitializing: true,
  requiresResovaLogin: false,
  user: null,
  account: null,
  impersonating: null,
  analyticsData: null,
  analyticsLoading: false,
  analyticsError: null,
  dateRange: 'last 7 days',  // Default to 7 days to match fast initial sync window
  syncInfo: null,
  conversationHistory: [],
  chatLoading: false,
  chatError: null,
  chatStreamingStatus: null,
  currentSessionId: null,
  chatSessions: [],
  chatSessionsLoading: false,
  favoritePrompts: [],
  favoritesLoading: false,
};

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AppState>(initialState);

  // Refs to avoid recreating sendMessage on every state change
  const conversationHistoryRef = useRef(state.conversationHistory);
  const analyticsDataRef = useRef(state.analyticsData);
  const currentSessionIdRef = useRef(state.currentSessionId);
  conversationHistoryRef.current = state.conversationHistory;
  analyticsDataRef.current = state.analyticsData;
  currentSessionIdRef.current = state.currentSessionId;

  // ============================================
  // Auth Actions
  // ============================================

  /**
   * Login with Resova API key
   * Server creates session and sets httpOnly cookie
   */
  const login = useCallback(async (apiKey: string): Promise<boolean> => {
    try {
      const response = await apiLogin(apiKey);

      if (!response.success || !response.user || !response.account) {
        console.error('Login failed:', response.error);
        return false;
      }

      setState(prev => ({
        ...prev,
        isAuthenticated: true,
        user: response.user || null,
        account: response.account || null,
      }));

      return true;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  }, []);

  /**
   * Set user directly (used by SignIn component after auth)
   */
  const setUser = useCallback((user: UserInfo | null) => {
    setState(prev => ({
      ...prev,
      user,
      isAuthenticated: user !== null,
    }));
  }, []);

  /**
   * Set account directly (used by SignIn component after auth)
   */
  const setAccount = useCallback((account: AccountInfo | null) => {
    setState(prev => ({
      ...prev,
      account,
    }));
  }, []);

  /**
   * Set impersonation state (used by AdminContext)
   */
  const setImpersonating = useCallback((impersonating: AppState['impersonating']) => {
    setState(prev => ({
      ...prev,
      impersonating,
    }));
  }, []);

  /**
   * Manually set requiresResovaLogin. Useful for testing and for forcing a
   * re-auth via Resova after a session is invalidated mid-flight.
   */
  const setRequiresResovaLogin = useCallback((value: boolean) => {
    setState(prev => ({ ...prev, requiresResovaLogin: value }));
  }, []);

  /**
   * Logout - server clears session and httpOnly cookie
   */
  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch (error) {
      console.error('Logout error:', error);
    }

    // Clear local state regardless of server response
    setState({
      ...initialState,
      isInitializing: false,
    });
  }, []);

  /**
   * Refresh auth state from server
   * Used to check if account setup has been completed, etc.
   */
  const refreshAuth = useCallback(async () => {
    try {
      const response = await getSession();

      if (response.success && response.authenticated && response.user && response.account) {
        setState(prev => ({
          ...prev,
          isAuthenticated: true,
          user: response.user || null,
          account: response.account || null,
          impersonating: response.impersonating || null,
        }));
      }
    } catch (error) {
      console.error('Auth refresh error:', error);
    }
  }, []);

  /**
   * Initialize auth on mount via a two-step flow (RP-2567):
   *
   * Step 1 — Check existing ResovAI session (GET /api/auth/me).
   *           Returning users with a valid session land directly in the app.
   *
   * Step 2 — If no ResovAI session, attempt cookie-based SSO by reading the
   *           Resova JWT from the browser cookie and POSTing it to
   *           /api/auth/init. On success the server issues a new ResovAI
   *           session cookie.
   *
   * If both steps fail, requiresResovaLogin is set to true so AuthGate can
   * redirect the user to the Resova login page (or fall back to /signin in
   * environments where VITE_RESOVA_LOGIN_URL is unset).
   */
  useEffect(() => {
    /**
     * Read a browser-accessible cookie value by name. Returns null when the
     * cookie is absent or document.cookie is unavailable (SSR / unit tests).
     */
    const getResovaTokenFromCookie = (): string | null => {
      try {
        const cookieName = import.meta.env.VITE_RESOVA_COOKIE_NAME || '_resova_admin_jwt';
        const match = document.cookie
          .split(';')
          .map(c => c.trim())
          .find(c => c.startsWith(`${cookieName}=`));
        return match ? decodeURIComponent(match.split('=').slice(1).join('=')) : null;
      } catch {
        return null;
      }
    };

    const initializeAuth = async () => {
      try {
        // Step 1: check for an existing ResovAI session
        const sessionResponse = await getSession();

        if (
          sessionResponse.success &&
          sessionResponse.authenticated &&
          sessionResponse.user &&
          sessionResponse.account
        ) {
          setState(prev => ({
            ...prev,
            isAuthenticated: true,
            isInitializing: false,
            requiresResovaLogin: false,
            user: sessionResponse.user || null,
            account: sessionResponse.account || null,
            impersonating: sessionResponse.impersonating || null,
          }));
          return;
        }

        // Step 2: attempt SSO init using the Resova cookie
        const resovaToken = getResovaTokenFromCookie();

        if (resovaToken) {
          try {
            const initResponse = await initWithResovaToken(resovaToken);

            if (initResponse.success && initResponse.user && initResponse.account) {
              setState(prev => ({
                ...prev,
                isAuthenticated: true,
                isInitializing: false,
                requiresResovaLogin: false,
                user: initResponse.user || null,
                account: initResponse.account || null,
                impersonating: null,
              }));
              return;
            }
          } catch (initError) {
            console.error('Resova SSO init failed:', initError);
          }
        }

        // Both steps failed — signal that the user must authenticate via Resova
        setState(prev => ({
          ...prev,
          isAuthenticated: false,
          isInitializing: false,
          requiresResovaLogin: true,
          user: null,
          account: null,
          impersonating: null,
        }));
      } catch (error) {
        console.error('Auth initialization error:', error);
        setState(prev => ({
          ...prev,
          isAuthenticated: false,
          isInitializing: false,
          requiresResovaLogin: false,
        }));
      }
    };

    initializeAuth();
  }, []);

  // ============================================
  // Analytics Actions
  // ============================================

  const fetchAnalytics = useCallback(async (dateRange?: string) => {
    if (!state.isAuthenticated) {
      console.warn('Not authenticated - skipping analytics fetch');
      return;
    }

    setState(prev => ({
      ...prev,
      analyticsLoading: true,
      analyticsError: null,
    }));

    try {
      // Always use current state dateRange if not provided
      const effectiveDateRange = dateRange || state.dateRange;
      const response = await apiFetchAnalytics({ dateRange: effectiveDateRange });

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch analytics');
      }

      setState(prev => ({
        ...prev,
        analyticsData: response.data || null,
        analyticsLoading: false,
        syncInfo: response.sync || null,
      }));
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch analytics';
      setState(prev => ({
        ...prev,
        analyticsLoading: false,
        analyticsError: errorMessage,
      }));
    }
  }, [state.isAuthenticated, state.dateRange]);

  const refreshAnalytics = useCallback(() => fetchAnalytics(state.dateRange), [fetchAnalytics, state.dateRange]);

  const setDateRange = useCallback((dateRange: string) => {
    setState(prev => ({ ...prev, dateRange }));
    // Automatically fetch analytics with new date range
    fetchAnalytics(dateRange);
  }, [fetchAnalytics]);

  // ============================================
  // Chat Actions
  // ============================================

  const sendMessage = useCallback(
    async (message: string): Promise<string | undefined> => {
      const currentHistory = conversationHistoryRef.current;
      const currentSessionId = currentSessionIdRef.current;

      // Add user message immediately
      setState(prev => ({
        ...prev,
        chatLoading: true,
        chatError: null,
        conversationHistory: [
          ...prev.conversationHistory,
          { role: 'user' as const, content: message },
        ],
      }));

      try {
        const response = await sendChatMessage({
          message,
          analyticsData: analyticsDataRef.current || undefined,
          conversationHistory: currentHistory,
          sessionId: currentSessionId || undefined,
        });

        if (!response.success) {
          throw new Error(response.message || 'Failed to send message');
        }

        // Add assistant response with metadata (charts, tables, suggestions)
        setState(prev => ({
          ...prev,
          conversationHistory: [
            ...prev.conversationHistory,
            {
              role: 'assistant' as const,
              content: response.message || '',
              charts: response.charts as ChartSpec[] | undefined,
              tables: response.tables,
              suggestedQuestions: response.suggestedQuestions,
            },
          ],
          chatLoading: false,
          currentSessionId: response.sessionId || prev.currentSessionId,
        }));

        return response.message;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to send message';
        setState(prev => ({
          ...prev,
          chatLoading: false,
          chatError: errorMessage,
        }));
        return undefined;
      }
    },
    []
  );

  const clearConversation = useCallback(() => {
    setState(prev => ({
      ...prev,
      conversationHistory: [],
      chatLoading: false,
      chatError: null,
      currentSessionId: null,
    }));
  }, []);

  const setConversationHistory = useCallback((messages: Message[]) => {
    setState(prev => ({
      ...prev,
      conversationHistory: messages,
    }));
  }, []);

  const loadChatSessions = useCallback(async () => {
    setState(prev => ({ ...prev, chatSessionsLoading: true }));
    try {
      const response = await getChatSessions();
      if (response.success && response.data) {
        setState(prev => ({
          ...prev,
          chatSessions: response.data || [],
          chatSessionsLoading: false,
        }));
      } else {
        setState(prev => ({ ...prev, chatSessionsLoading: false }));
      }
    } catch (error) {
      console.error('Failed to load chat sessions:', error);
      setState(prev => ({ ...prev, chatSessionsLoading: false }));
    }
  }, []);

  const loadChatSession = useCallback(async (sessionId: string) => {
    // Set the target session immediately so stale responses can be detected
    setState(prev => ({ ...prev, chatLoading: true, currentSessionId: sessionId }));
    try {
      const response = await getChatSession(sessionId);
      if (response.success && response.data) {
        const messages: Message[] = (response.data.messages || []).map(m => ({
          role: m.role as 'user' | 'assistant',
          content: m.content,
        }));
        // Guard: only apply if this is still the active session (prevents race condition)
        setState(prev => {
          if (prev.currentSessionId !== sessionId) return prev;
          return {
            ...prev,
            conversationHistory: messages,
            chatLoading: false,
          };
        });
      } else {
        setState(prev => {
          if (prev.currentSessionId !== sessionId) return prev;
          return { ...prev, chatLoading: false };
        });
      }
    } catch (error) {
      console.error('Failed to load chat session:', error);
      setState(prev => {
        if (prev.currentSessionId !== sessionId) return prev;
        return { ...prev, chatLoading: false };
      });
    }
  }, []);

  const deleteSession = useCallback(async (sessionId: string) => {
    try {
      await deleteChatSession(sessionId);
      setState(prev => ({
        ...prev,
        chatSessions: prev.chatSessions.filter(s => s.id !== sessionId),
        // If we deleted the current session, clear conversation
        ...(prev.currentSessionId === sessionId ? {
          currentSessionId: null,
          conversationHistory: [],
        } : {}),
      }));
    } catch (error) {
      console.error('Failed to delete chat session:', error);
    }
  }, []);

  const startNewSession = useCallback(() => {
    setState(prev => ({
      ...prev,
      conversationHistory: [],
      currentSessionId: null,
      chatError: null,
    }));
  }, []);

  // ============================================
  // Favorites Actions
  // ============================================

  const loadFavorites = useCallback(async () => {
    setState(prev => ({ ...prev, favoritesLoading: true }));
    try {
      const response = await apiGetFavorites();
      if (response.success && response.data) {
        setState(prev => ({
          ...prev,
          favoritePrompts: response.data || [],
          favoritesLoading: false,
        }));
      } else {
        setState(prev => ({ ...prev, favoritesLoading: false }));
      }
    } catch (error) {
      console.error('Failed to load favorites:', error);
      setState(prev => ({ ...prev, favoritesLoading: false }));
    }
  }, []);

  const addFavorite = useCallback(async (params: AddFavoriteParams): Promise<boolean> => {
    try {
      const response = await apiAddFavorite(params);
      if (response.success && response.data) {
        // Use the full favorites array from server to ensure sync and prevent duplicates
        setState(prev => ({
          ...prev,
          favoritePrompts: response.data?.favorites || prev.favoritePrompts,
        }));
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to add favorite:', error);
      return false;
    }
  }, []);

  const removeFavorite = useCallback(async (id: string) => {
    try {
      const response = await apiDeleteFavorite(id);
      if (response.success && response.data) {
        // Use the full favorites array from server to ensure sync
        setState(prev => ({
          ...prev,
          favoritePrompts: response.data || prev.favoritePrompts.filter(f => f.id !== id),
        }));
      }
    } catch (error) {
      console.error('Failed to remove favorite:', error);
    }
  }, []);

  const updateFavoritesOrder = useCallback(async (favorites: FavoritePrompt[]) => {
    try {
      const response = await apiUpdateFavorites(favorites);
      if (response.success && response.data) {
        setState(prev => ({
          ...prev,
          favoritePrompts: response.data || favorites,
        }));
      }
    } catch (error) {
      console.error('Failed to update favorites:', error);
    }
  }, []);

  const isFavorite = useCallback((text: string): boolean => {
    return state.favoritePrompts.some(f => f.text.toLowerCase() === text.toLowerCase());
  }, [state.favoritePrompts]);

  // Load favorites when authenticated or when user changes
  useEffect(() => {
    if (state.isAuthenticated && !state.isInitializing) {
      loadFavorites();
    }
  }, [state.isAuthenticated, state.isInitializing, loadFavorites, state.user?.id]);

  // ============================================
  // Context Value
  // ============================================

  const value: AppContextType = {
    ...state,
    // Derived - for backwards compatibility
    userName: state.user?.name || state.account?.name || null,
    login,
    logout,
    refreshAuth,
    setUser,
    setAccount,
    setImpersonating,
    setRequiresResovaLogin,
    fetchAnalytics,
    refreshAnalytics,
    setDateRange,
    sendMessage,
    clearConversation,
    setConversationHistory,
    loadChatSessions,
    loadChatSession,
    deleteSession,
    startNewSession,
    loadFavorites,
    addFavorite,
    removeFavorite,
    updateFavorites: updateFavoritesOrder,
    isFavorite,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

// ============================================
// Hooks
// ============================================

export function useApp() {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
}

export function useAuth() {
  const {
    isAuthenticated,
    isInitializing,
    requiresResovaLogin,
    user,
    account,
    impersonating,
    login,
    logout,
    setImpersonating,
    setRequiresResovaLogin,
  } = useApp();
  return {
    isAuthenticated,
    isInitializing,
    requiresResovaLogin,
    user,
    account,
    impersonating,
    login,
    logout,
    setImpersonating,
    setRequiresResovaLogin,
  };
}

export function useAccount() {
  const { account, setAccount } = useApp();
  return { account, setAccount };
}

export function useAnalytics() {
  const { analyticsData, analyticsLoading, analyticsError, dateRange, syncInfo, fetchAnalytics, refreshAnalytics, setDateRange } = useApp();
  return { analyticsData, analyticsLoading, analyticsError, dateRange, syncInfo, fetchAnalytics, refreshAnalytics, setDateRange };
}

export function useChat() {
  const {
    conversationHistory,
    chatLoading,
    chatError,
    chatStreamingStatus,
    currentSessionId,
    chatSessions,
    chatSessionsLoading,
    sendMessage,
    clearConversation,
    setConversationHistory,
    loadChatSessions,
    loadChatSession,
    deleteSession,
    startNewSession,
  } = useApp();
  return {
    conversationHistory,
    chatLoading,
    chatError,
    chatStreamingStatus,
    currentSessionId,
    chatSessions,
    chatSessionsLoading,
    sendMessage,
    clearConversation,
    setConversationHistory,
    loadChatSessions,
    loadChatSession,
    deleteSession,
    startNewSession,
  };
}

export function useFavorites() {
  const {
    favoritePrompts,
    favoritesLoading,
    loadFavorites,
    addFavorite,
    removeFavorite,
    updateFavorites,
    isFavorite,
  } = useApp();
  return {
    favoritePrompts,
    favoritesLoading,
    loadFavorites,
    addFavorite,
    removeFavorite,
    updateFavorites,
    isFavorite,
  };
}
